import { CommonModule } from '@angular/common';
import { Component, Input, inject, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { ReviewActionType, TriggeredRuleItem, UpdateReviewTriggeredRuleRequest } from '@bn/app-shield';
import { DecodeURIPipe } from '@bn/shared/entities/index';
import { StoreRequest } from '@bn/shared/state-store';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ChartReMapData } from '@bn/web-ui/chart';
import { InfoCardComponent } from '@bn/web-ui/info-card/info-card.component';
import { PlanType } from '../../enums';
import { ShieldLogsItemLogModel, ShieldLogsItemModel } from '../../models';
import { ShieldApiStore } from '../../stores';
import { AppShieldEventLogsWafComponent } from '../app-shield-event-logs/app-shield-event-logs-waf.component';
import { ChartVolumeOfAttacksComponent } from '../chart-volume-of-attacks/chart-volume-of-attacks.component';

@Component({
  selector: 'app-app-shield-waf-triggered-rules-slideout',
  templateUrl: './app-shield-waf-triggered-rules-slideout.component.html',
  styleUrls: ['./app-shield-waf-triggered-rules-slideout.component.scss'],
  imports: [
    CommonModule,
    AppShieldEventLogsWafComponent,
    DecodeURIPipe,
    BadgeComponent,
    ButtonComponent,
    CardComponent,
    InfoCardComponent,
    ChartVolumeOfAttacksComponent,
    FaIconComponent,
    NgxSkeletonLoaderComponent,
  ],
})
export class AppShieldWafTriggeredRulesSlideoutComponent {
  readonly #shieldStore = inject(ShieldApiStore);

  public readonly isLoading = this.#shieldStore.isLoading(StoreRequest.Update, ['TriggeredRules']);
  public readonly isChartLoading = this.#shieldStore.isLoading(StoreRequest.Item, ['TriggeredRuleMetrics']);
  public readonly isLoadingAIRecommendations = this.#shieldStore.isLoading(StoreRequest.Item, ['AI']);
  public readonly getSelectedShieldId = this.#shieldStore.selectedEntityId();
  public readonly selectedShield = this.#shieldStore.selectedEntity();
  public readonly chartMetrics = this.#shieldStore.chartMetrics;
  public readonly aiRecommendationText = this.#shieldStore.aiRecommendationText;

  public readonly directionClick = output<'prev' | 'next'>();

  @Input()
  public set triggeredRule(data: TriggeredRuleItem | null) {
    if (!data) {
      this.noData = true;

      return;
    }

    // Note: clear AI recommendations only if rule was changed, if it is the same we keep it
    if (data !== this.internalTriggeredRule) {
      this.#shieldStore.clearAiRecommendation();
    }

    this.logs = data?.ruleLogs?.map((log) => ({
      ...log,
      log: <ShieldLogsItemLogModel>JSON.parse(log.log as unknown as string),
    })) as ShieldLogsItemModel[];

    this.internalTriggeredRule = data;
    this.topTargetedUrls = data?.topTargetedUrls;
    this.topTargetedUrlKeys = Object.keys(data?.topTargetedUrls ?? []);
  }

  @Input()
  public set data(obj: ShieldLogsItemModel[]) {
    this.logs = obj.map((log) => ({
      ...log,
      log: <ShieldLogsItemLogModel>JSON.parse(log.log as unknown as string),
    })) as ShieldLogsItemModel[];
  }

  @Input()
  public selectedRuleIndex?: number = undefined;

  @Input()
  public rulesCount?: number = undefined;

  public logs: ShieldLogsItemModel[] | null = null;
  public internalTriggeredRule: TriggeredRuleItem | null = null;
  public reviewActionType = ReviewActionType;
  public shieldId?: number;
  public topTargetedUrls: TriggeredRuleItem['topTargetedUrls'] | null = null;
  public topTargetedUrlKeys: string[] = [];
  public noData = false;
  public hasChartData = false;
  public planType = PlanType;
  public chartData: ChartReMapData = {
    chartSeries: [['time', 'Requests', 'Triggered Rules']],
    total: {
      totalTriggers: 0,
      blocked: 0,
      logged: 0,
      challenged: 0,
    },
  };

  constructor() {
    this.subscribeToStoreEvents();
  }

  public setRuleAction(ruleId: UpdateReviewTriggeredRuleRequest['ruleId'], action: ReviewActionType): void {
    this.#shieldStore.setTriggeredRule({ id: Number(this.getSelectedShieldId()), data: { ruleId, action } });
  }

  public onDirectionClick(direction: 'prev' | 'next') {
    if ((this.rulesCount || 0) > 1) this.directionClick.emit(direction);
  }

  public getRecomendations(shieldZoneId?: string | number, ruleId?: string | null): void {
    if (!shieldZoneId || shieldZoneId === '' || !ruleId || ruleId === '') return;

    this.#shieldStore.getAIRecommendations({
      shieldZoneId: Number(shieldZoneId),
      ruleId,
    });
  }

  private subscribeToStoreEvents(): void {
    toObservable(this.chartMetrics)
      .pipe(takeUntilDestroyed())
      .subscribe((chartMetrics) => {
        if (!chartMetrics) return;

        if (chartMetrics.wafTriggeredRuleChartData) {
          this.chartData.chartSeries = chartMetrics.wafTriggeredRuleChartData.chartSeries;
          this.chartData.total = chartMetrics.wafTriggeredRuleChartData.total;
          this.hasChartData = chartMetrics.wafTriggeredRuleChartData.hasData;
        }
      });
  }
}
